import React, { useState } from 'react';
import TrendPercentIndicator from '@src/components/shared/TrendPercentIndicator/TrendPercentIndicator';
import './TrendCard.scss';
import { formatNumber, formatPercentNumber } from '@src/utils/number-util';

export interface TrendDataPoint {
  timestamp: number;
  count: number;
}

export interface TrendCardProps {
  total: number;
  title: React.ReactNode;
  trendPercent: number;
  trendLabel: string;
  trendColor: string;
  data: TrendDataPoint[];
  ratePercent?: number;
}

// Generate a smooth SVG path using cubic Bezier curves
const getSmoothPath = (pts: { x: number; y: number }[]) => {
  if (pts.length < 2) return '';
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    // Calculate control points for smoothness
    const cp1x = p0.x + (p1.x - p0.x) / 2;
    const cp1y = p0.y;
    const cp2x = p0.x + (p1.x - p0.x) / 2;
    const cp2y = p1.y;
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p1.x},${p1.y}`;
  }
  return d;
};

const TrendCard: React.FC<TrendCardProps> = ({
  total,
  title,
  trendPercent,
  trendLabel,
  trendColor,
  data,
  ratePercent,
}) => {

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const width = 135;
  const height = 80;

  // Hover hitbox size (kept constant for consistent hover behavior)
  const HOVER_RECT_WIDTH = 12;
  const HOVER_RECT_HEIGHT = 16;
  const HOVER_RECT_HALF_HEIGHT = HOVER_RECT_HEIGHT / 2;

  const counts = data.map(d => d.count);
  const max = Math.max(...counts);
  const min = Math.min(...counts);
  const range = max - min;
  const points = data
      .map((d, i, arr) => {
        const x = (i / (arr.length - 1)) * width;
        const y = range === 0
            ? height / 2
            : height - ((d.count - min) / range) * height;
        return { x, y, point: d };
      });

  const pathD = getSmoothPath(points);
  const gradientId = `trend-gradient-${trendColor.replace('#', '')}`;

  const getTooltip = (idx: number) => {
    const point = points[idx].point;
    const date = new Date(point.timestamp);
    const dateStr = `${date.toLocaleString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    })} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    return (
      <div className="trend-card-tooltip">
        <div className="trend-card-tooltip-date">{dateStr}</div>
        <div className="trend-card-tooltip-value">{formatNumber(point.count)}</div>
      </div>
    );
  };

  return (
    <div className="trend-card">
      <div className="trend-card-info">
        <div className="trend-card-total">
          {formatNumber(total)}
          {ratePercent ?
              <div className="trend-card-total_percent">
                <span>{formatPercentNumber(ratePercent)}%</span>
                <span>of total traffic</span>
              </div> :
              <></>}
        </div>
        <div className="trend-card-title">{title}</div>
        <TrendPercentIndicator
          trendPercent={trendPercent}
          trendLabel={trendLabel}
        />
      </div>
      <div className="trend-card-graph-wrapper" style={{ position: 'relative' }}>
        <svg width={width} height={height} className="trend-card-graph" aria-hidden="true">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={trendColor} stopOpacity="0.5" />
              <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={pathD}
            fill="none"
            stroke={trendColor}
            strokeWidth={2}
          />
          <path d={pathD + ` L${width},${height} L0,${height} Z`} fill={`url(#${gradientId})`}/>
          {points.map((pt, idx) => {
            // Keep the hover rect fully inside the SVG bounds.
            const rectY = Math.min(
              Math.max(pt.y - HOVER_RECT_HALF_HEIGHT, 0),
              height - HOVER_RECT_HEIGHT,
            );

            return (
              <rect
                key={data[idx]?.timestamp ?? idx}
                x={pt.x - HOVER_RECT_WIDTH / 2}
                y={rectY}
                width={HOVER_RECT_WIDTH}
                height={HOVER_RECT_HEIGHT}
                fill="transparent"
                onMouseEnter={() => setHoverIdx(idx)}
                onMouseLeave={() => setHoverIdx(null)}
                style={{ cursor: 'pointer' }}
              />
            );
          })}
        </svg>
        {hoverIdx !== null && (
          (() => {
            const tooltipWidth = 140;
            const tooltipHeight = 45;
            const left = points[hoverIdx].x - tooltipWidth / 2;
            const arrowHeight = 8;
            const top = points[hoverIdx].y - tooltipHeight - arrowHeight;
            return (
              <div
                className="trend-card-tooltip-wrapper"
                style={{
                  position: 'absolute',
                  left,
                  top,
                  width: tooltipWidth,
                  pointerEvents: 'none',
                  zIndex: 10,
                }}
              >
                {getTooltip(hoverIdx)}
                <div className="trend-card-tooltip-arrow-top" />
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default TrendCard;
